import React from 'react';
import { Wrench, Zap, Cpu, Compass, Play, BookOpen, Clock } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { TrackSummary, TrackType } from '../../../shared/types';

export interface TrackCardProps {
  track: TrackSummary;
  onSelectTrack: (track: TrackType) => void;
  onStartStudy: (track: TrackType) => void;
  onStartMock: (track: TrackType) => void;
}

export function TrackCard({
  track,
  onSelectTrack,
  onStartStudy,
  onStartMock,
}: TrackCardProps) {
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'Wrench':
        return <Wrench className="h-6 w-6 text-amber-400" aria-hidden="true" />;
      case 'Zap':
        return <Zap className="h-6 w-6 text-sky-400" aria-hidden="true" />;
      case 'Cpu':
        return <Cpu className="h-6 w-6 text-emerald-400" aria-hidden="true" />;
      case 'Compass':
        return <Compass className="h-6 w-6 text-purple-400" aria-hidden="true" />;
      default:
        return <Wrench className="h-6 w-6 text-amber-400" aria-hidden="true" />;
    }
  };

  const getBorderHover = (trackType: TrackType) => {
    switch (trackType) {
      case 'mechanical':
        return 'hover:border-amber-500/50 hover:shadow-amber-500/10';
      case 'electrical':
        return 'hover:border-sky-500/50 hover:shadow-sky-500/10';
      case 'multicraft':
        return 'hover:border-emerald-500/50 hover:shadow-emerald-500/10';
      case 'aptitude':
        return 'hover:border-purple-500/50 hover:shadow-purple-500/10';
      default:
        return 'hover:border-amber-500/50';
    }
  };

  const handleTrackLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
      e.preventDefault();
      onSelectTrack(track.track);
    }
  };

  return (
    <article className={`relative flex flex-col justify-between rounded-xl border border-border dark:border-slate-800 bg-card dark:bg-slate-900/60 p-6 shadow-sm dark:shadow-lg transition-all duration-200 ${getBorderHover(track.track)} backdrop-blur-sm group`}>
      <div>
        {/* Top Header */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/60 group-hover:scale-105 transition-transform">
              {getIcon(track.iconName)}
            </div>
            <div>
              <Badge variant={track.track as any}>
                {track.track}
              </Badge>
              <h3 className="mt-1 text-lg font-bold text-foreground dark:text-white">
                <a
                  href={`/track/${track.track}`}
                  onClick={handleTrackLinkClick}
                  className="hover:text-amber-500 dark:hover:text-amber-400 transition-colors"
                >
                  {track.title}
                </a>
              </h3>
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-xs leading-relaxed text-muted-foreground mb-5">
          {track.description}
        </p>

        {/* Categories Pills */}
        <div className="mb-6 space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Core Modules ({track.categories.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {track.categories.slice(0, 4).map((cat) => (
              <span
                key={cat.id}
                className="inline-flex items-center rounded-md bg-slate-100 dark:bg-slate-800/80 px-2 py-1 text-[11px] text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700/50"
              >
                {cat.name}
              </span>
            ))}
            {track.categories.length > 4 && (
              <span className="inline-flex items-center rounded-md bg-slate-100/60 dark:bg-slate-800/40 px-2 py-1 text-[11px] text-muted-foreground">
                +{track.categories.length - 4} more
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="border-t border-border dark:border-slate-800/80 pt-4 mt-auto">
        <div className="flex items-center justify-between mb-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1 font-mono">
            <BookOpen className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            {track.totalQuestions} Questions
          </span>
          <span className="flex items-center gap-1 font-mono">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            60 Min Mock
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onStartStudy(track.track)}
            className="w-full text-xs gap-1"
          >
            <BookOpen className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400" aria-hidden="true" />
            Study Mode
          </Button>

          <Button
            variant="amber"
            size="sm"
            onClick={() => onStartMock(track.track)}
            className="w-full text-xs gap-1"
          >
            <Play className="h-3.5 w-3.5" aria-hidden="true" />
            Timed Mock
          </Button>
        </div>
      </div>
    </article>
  );
}
